import {
  Button,
  Dialog,
  ProgressBar,
  useToast,
} from '@sl/react-datafabric-components';
import { InputNumber } from 'primereact/inputnumber'; // @sl version uses onValueChange which does not work
import { FC, useEffect, useState } from 'react';
import { useGetProductByTypeQuery } from '../../../../common/redux/services/products';
import { useCreatePurchaseSessionMutation } from '../../../../common/redux/services/subscriptions';
import { useCreateTransactionMutation } from '../../../../common/redux/services/transactions';
import { useFetchCurrentUsersQuery } from '../../../../common/redux/services/users';

interface BuyTokensDialogProps {
  visible: boolean;
  onHide: () => void;
  subscriptionData: any; // The rowData for the subscription
}

export const BuyTokensDialog: FC<BuyTokensDialogProps> = ({
  visible,
  onHide,
  subscriptionData,
}) => {
  const [quantity, setQuantity] = useState<number | null>(100); // Default quantity
  const { showToast } = useToast();
  const { data: user } = useFetchCurrentUsersQuery();

  const {
    data: productData,
    isLoading: isLoadingTokenPrice,
    error: tokenPriceError,
  } = useGetProductByTypeQuery('token');

  const priceId = productData?.data?.metadata?.priceId;
  const customerId = subscriptionData?.providerMetadata?.customerId;

  const [createPurchaseSession, { isLoading: isCreatingCheckoutSession }] =
    useCreatePurchaseSessionMutation();

  const [createTransaction] = useCreateTransactionMutation();

  const pricePerToken = productData?.data.cost;

  const totalPrice = quantity && pricePerToken ? quantity * pricePerToken : 0;

  useEffect(() => {
    if (tokenPriceError) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: 'Could not fetch credit price.',
        },
        'top-right'
      );
    }
  }, [tokenPriceError, showToast]);

  const handleProceedToPayment = async () => {
    if (!quantity || quantity <= 0) {
      showToast(
        {
          severity: 'warn',
          summary: 'Invalid Quantity',
          detail: 'Please enter a valid number of credits to purchase.',
        },
        'top-right'
      );
      return;
    }

    if (!subscriptionData?.id) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: 'Subscription ID is missing.',
        },
        'top-right'
      );
      return;
    }
    try {
      const transaction = {
        type: 'tokens',
        provider: 'stripe',
        value: totalPrice,
        quantity: quantity,
        productID: productData.data.id,
        companyID: user.data?.companyID,
      };
      const { data: createdTransaction } = await createTransaction(
        transaction
      ).unwrap();

      const returnUrl = `${window.location.origin}/subscriptions/manage-subscription?buy_tokens_return=true&transactionId=${createdTransaction.id}`;
      const response = await createPurchaseSession({
        priceId,
        customerId,
        returnUrl, //replace in the BE
        quantity,
        transactionId: createdTransaction.id,
        subscriptionID: subscriptionData.id,
      }).unwrap();

      //TODO: update

      if (response.data.url) {
        window.location.href = response.data.url; // Redirect to Stripe Checkout
      } else if (response.error) {
        // Error already shown by the mutation hook's toast
        console.error('Failed to create Stripe session.');
      } else {
        showToast(
          {
            severity: 'error',
            summary: 'Error',
            detail: 'Could not retrieve checkout session URL.',
          },
          'top-right'
        );
      }
    } catch (error) {
      // This catch might be redundant if the mutation handles its own errors with toasts
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: `Failed to initiate credit purchase. Error: ${error}`,
        },
        'top-right'
      );
    }
  };

  const dialogFooter = (
    <div className='flex justify-content-end gap-2'>
      <Button
        label='Cancel'
        icon='pi pi-times'
        onClick={onHide}
        className='p-button-text'
        disabled={isCreatingCheckoutSession}
      />
      <Button
        label='Proceed to Payment'
        icon='pi pi-check'
        onClick={handleProceedToPayment}
        loading={isCreatingCheckoutSession || isLoadingTokenPrice}
        disabled={!pricePerToken || pricePerToken <= 0}
      />
    </div>
  );

  return (
    <Dialog
      header='Buy Additional Credits'
      visible={visible}
      style={{ width: '450px' }}
      //   modal
      footer={dialogFooter}
      onHide={onHide}
    >
      {isLoadingTokenPrice && (
        <div className='text-center'>
          <p>Loading credit price...</p>
          <ProgressBar mode='indeterminate' style={{ height: '6px' }} />
        </div>
      )}
      {!isLoadingTokenPrice && tokenPriceError && (
        <p className='text-red-500'>
          Could not load credit price. Please try again.
        </p>
      )}
      {!isLoadingTokenPrice && !tokenPriceError && pricePerToken > 0 && (
        <div className='p-fluid'>
          <div className='field'>
            <label htmlFor='quantity'>Number of credits</label>
            <InputNumber
              id='quantity'
              value={quantity}
              onChange={(e) => {
                setQuantity(e.value);
              }}
              min={1}
              mode='decimal'
              className='w-full'
              placeholder='Enter number of credits'
            />
          </div>
          <div className='field mt-5 '>
            <p>
              Price per credit:{' '}
              <strong>
                {pricePerToken ? pricePerToken.toFixed(2) : 'N/A'}€
              </strong>
            </p>
            <p>
              Total Price:{' '}
              <strong>{totalPrice ? totalPrice.toFixed(2) : '0.00'}€</strong>
            </p>
          </div>
        </div>
      )}
      {!isLoadingTokenPrice && !tokenPriceError && !pricePerToken && (
        <p className='text-orange-500'>
          Credit pricing information is currently unavailable.
        </p>
      )}
    </Dialog>
  );
};
